import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';
import { RootState } from '../app/store';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { MAX_CART_ITEM_QUANTITY } from '../constants';
import { addLoyaltiesCode, clearCart } from '../reducers/cartSlice';
import { addHypothesisItemToCart } from '../reducers/menuSlice';
import { orderActions } from '../reducers/orderSlice';
import { normalizeMenuModuleError } from '../utils/menuModuleError';
import { selectIsAIAutoMode } from '../redux/features/ai/ai.selector';
import {
  selectFinalOrderSubmitted,
  selectSessionId,
} from '../redux/features/sessionBoundary/sessionBoundary.selectors';
import { selectHypothesisFrame } from '../selectors/message';
import { resetGroupedUpsellDeclined } from '../utils/groupedUpsellDeclined';
import { getMenuUnificationFeatureFlag } from '../utils/session-storage';
import { EntityMenuItem } from '../reducers/messagingSlice';
import { convertBMOrderItemToEntityItem } from '../utils/menuUnification.utils';
import { CartItemAdditionTypes } from '../constants/event';
import { ICartLoyalty } from '../reducers/cartSlice.props';
import { selectBrandMenuConstraints } from '../selectors/menu';
import { selectFeatureFlagMenuModuleErrorUi } from '../redux/features/config/config.selector';
import logger from '../utils/logger';

const useHypothesisFrames = () => {
  const dispatch = useAppDispatch();
  // Non-subscribing store access: the cart-emptiness check below must read the
  // cart at frame-arrival time without re-running this effect on cart changes
  // (the replay itself mutates the cart, which would loop).
  const store = useStore<RootState>();
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const hypothesisFrame = useAppSelector(selectHypothesisFrame);
  const isFinalOrderSubmitted = useAppSelector(selectFinalOrderSubmitted);
  const sbSessionId = useAppSelector(selectSessionId);
  const brandMenuConstraints = useAppSelector(selectBrandMenuConstraints);
  const menuModuleErrorUiEnabled = useAppSelector(
    selectFeatureFlagMenuModuleErrorUi
  );

  const shouldAddHypothesisItems =
    isAIAutoMode &&
    hypothesisFrame &&
    hypothesisFrame.session_id === sbSessionId;

  const hitlBrandMenuMode = getMenuUnificationFeatureFlag();

  // Fingerprint of the last hypothesis content applied to the cart. The AI
  // re-sends hypothesis frames as the conversation progresses, usually with
  // unchanged content; rebuilding the cart for those frames is wasted work
  // that lags HITL on items with large constraint sets (e.g. Crowd Pleasers).
  const lastAppliedHypothesesRef = useRef('');

  // Import the AI cart at the agent's interception (the isAIAutoMode true->false flip): the first unmarked frame as a fallback, then the marked handoff frame, which wins and ends importing — so this can apply up to twice, not strictly once.
  const prevIsAIAutoModeRef = useRef(isAIAutoMode);
  const prevSessionIdRef = useRef(sbSessionId);
  const agentInterceptedRef = useRef(false);
  const importedAtInterceptRef = useRef(false);
  // The gpt-context handoff flush marks its frame (handoff_flush); once we import
  // that marked final frame we are done, regardless of arrival order (Fix #3).
  const importedMarkedRef = useRef(false);
  const agentHasEditedRef = useRef(false);
  const backfilledRef = useRef(false);
  // True while our own intercept import (clearCart + async rebuild) is in flight.
  // The edit-guard is suppressed during this window and the baseline is recaptured
  // only once the import's thunks SETTLE — so an agent delete after the import is
  // detected against the full rebuilt cart, not a mid-flight (empty) snapshot that
  // would forgive it (the addHypothesisItemToCart async-settle resurrection, T2).
  const importingRef = useRef(false);
  // Baseline of the AI-authored cart at arm; any later agent edit disarms the import.
  const armBaselineRef = useRef<null | {
    keys: string[];
    items: RootState['cart']['cartItems'];
    qty: RootState['cart']['cartItemsQuantity'];
    couponCount: number;
    name: string;
  }>(null);

  useEffect(() => {
    const cart = store.getState().cart;
    const customerName = store.getState().order.customerName;
    const AI = CartItemAdditionTypes.AI;
    // Read fresh from the store so a re-baseline after our async import reflects
    // the settled cart, not this effect run's opening snapshot.
    const captureBaseline = () => {
      const c = store.getState().cart;
      armBaselineRef.current = {
        keys: Object.keys(c.cartItems),
        items: c.cartItems,
        qty: c.cartItemsQuantity,
        couponCount: Object.keys(c.coupons).length,
        name: store.getState().order.customerName,
      };
    };

    // New car: drop every latch so a prior takeover never leaks across sessions.
    if (prevSessionIdRef.current !== sbSessionId) {
      prevSessionIdRef.current = sbSessionId;
      agentInterceptedRef.current = false;
      importedAtInterceptRef.current = false;
      importedMarkedRef.current = false;
      agentHasEditedRef.current = false;
      backfilledRef.current = false;
      importingRef.current = false;
      prevIsAIAutoModeRef.current = isAIAutoMode;
      lastAppliedHypothesesRef.current = '';
    }

    // Arm on the true->false interception; agentHasEditedRef is deliberately NOT
    // reset here, so a completed takeover stays latched across any re-flip.
    if (prevIsAIAutoModeRef.current && !isAIAutoMode) {
      agentInterceptedRef.current = true;
      importedAtInterceptRef.current = false;
      importedMarkedRef.current = false;
      importingRef.current = false;
      captureBaseline();
    }
    prevIsAIAutoModeRef.current = isAIAutoMode;

    // Disarm on the agent's first real edit vs the arm baseline: authorship
    // (add/coupon/loyalty/name) plus a structural delete/modify/qty check against
    // the baseline keys. Skipped while our own import is in flight (importingRef) —
    // its clearCart+rebuild churn would otherwise read as an agent delete; the
    // baseline is recaptured post-settle so a real agent edit still trips it.
    const base = armBaselineRef.current;
    if (
      agentInterceptedRef.current &&
      !agentHasEditedRef.current &&
      base &&
      !importingRef.current
    ) {
      const authored =
        Object.values(cart.cartItems).some(
          (i) => i?.addedBy && i.addedBy !== AI
        ) ||
        cart.loyalties.some((l) => l?.addedBy && l.addedBy !== AI) ||
        Object.keys(cart.coupons).length !== base.couponCount ||
        customerName !== base.name;
      const structural = base.keys.some(
        (k) =>
          !(k in cart.cartItems) ||
          cart.cartItems[k] !== base.items[k] ||
          cart.cartItemsQuantity[k] !== base.qty[k]
      );
      if (authored || structural) {
        agentHasEditedRef.current = true;
      }
    }

    const sessionMatches =
      !!hypothesisFrame && hypothesisFrame.session_id === sbSessionId;

    // Prefer the marked handoff frame (deterministic, any arrival order); fall
    // back to the first distinct frame when unmarked. Spurious isAIAutoMode flips
    // (upsell TTS_SENT menuSlice.ts:1174, failed menu load) can arm and consume
    // that unmarked fallback mid-conversation, so the !6555 handoff_flush marker
    // is a hard reliability dependency here, not just deploy-order safety.
    const frameIsMarked = !!hypothesisFrame?.data?.hypotheses?.some(
      (h) => h.handoff_flush
    );
    const importAtIntercept =
      agentInterceptedRef.current &&
      !importedMarkedRef.current &&
      !agentHasEditedRef.current &&
      sessionMatches &&
      (frameIsMarked || !importedAtInterceptRef.current);

    // Remount AFTER the flip sees no transition; backfill an EMPTY cart once,
    // cartIsEmpty-guarded so it can never clobber an agent edit.
    const remountBackfill =
      !agentInterceptedRef.current &&
      !isAIAutoMode &&
      !agentHasEditedRef.current &&
      !backfilledRef.current &&
      sessionMatches &&
      Object.keys(cart.cartItems).length === 0;

    if (
      !isFinalOrderSubmitted &&
      (shouldAddHypothesisItems || importAtIntercept || remountBackfill)
    ) {
      // The flags change how a frame is applied, so a flip must invalidate the
      // fingerprint even when the hypothesis content is unchanged.
      // Fingerprint ONLY the fields the replay below actually consumes. The
      // frame also carries volatile fields (e.g. `dialog`, a growing session
      // transcript with timestamps) that differ on every send — hashing the
      // whole hypothesis object would make every frame look new and the skip
      // would never fire (HITL-1422).
      const hypothesesFingerprint = [
        sbSessionId,
        hitlBrandMenuMode,
        menuModuleErrorUiEnabled,
        JSON.stringify(
          hypothesisFrame?.data?.hypotheses?.map((hypothesis) => ({
            order_items: hypothesis.order_items,
            brand_menu_order_items: hypothesis.brand_menu_order_items,
            is_brand_menu: hypothesis.is_brand_menu,
            loyalties: hypothesis.loyalties,
            customer_info: hypothesis.customer_info,
            menu_module_error: hypothesis.menu_module_error,
          })) ?? null
        ),
      ].join('::');
      if (hypothesesFingerprint === lastAppliedHypothesesRef.current) {
        const frameHasItems = !!hypothesisFrame?.data?.hypotheses?.some(
          (hypothesis) =>
            hypothesis.order_items?.length ||
            hypothesis.brand_menu_order_items?.length
        );
        const cartIsEmpty =
          Object.keys(store.getState().cart.cartItems).length === 0;
        // Skip an already-applied frame UNLESS the cart is empty and the frame
        // has items (rebuild it). Returning here leaves the intercept one-shot
        // armed for the true final frame; the empty-cart rebuild covers [4].
        // importAtIntercept is excluded: an agent who empties the cart in auto
        // mode then takes over must not have the same frame refilled — a distinct
        // (marked) handoff frame has a fresh fingerprint and bypasses this skip.
        if (!frameHasItems || !cartIsEmpty || importAtIntercept) {
          return;
        }
      }
      lastAppliedHypothesesRef.current = hypothesesFingerprint;
      // A real import ran -> close the matching one-shot (a dedup skip returns above, leaving it armed).
      if (importAtIntercept) {
        logger.info({
          message: 'Imported AI hypothesis cart',
          path: frameIsMarked ? 'handoff_flush' : 'intercept',
          frameId: hypothesisFrame?.id,
        });
        importedAtInterceptRef.current = true;
        importingRef.current = true;
        // A marked frame is the terminal final cart -> stop importing entirely.
        if (frameIsMarked) {
          importedMarkedRef.current = true;
        }
      }
      if (remountBackfill) {
        backfilledRef.current = true;
      }

      // Collected so an intercept import can re-baseline only after the async
      // item thunks settle (see importingRef).
      const importItemPromises: Array<Promise<unknown>> = [];
      hypothesisFrame?.data?.hypotheses?.forEach((hypothesis) => {
        const {
          order_items: orderItems,
          brand_menu_order_items: brandMenuOrderItems,
          is_brand_menu: aiBrandMenuMode,
          loyalties = [],
          customer_info,
          menu_module_error: menuModuleError,
        } = hypothesis;

        dispatch(clearCart()); // Clear the cart and build a new one to avoid the comparison between cart items and order items when hypothesis evolves(update quantity or modifiers)
        resetGroupedUpsellDeclined(); // hypothesis replay rebuilds the cart from scratch, so suppress state must be recomputed from the new selectModifier calls
        dispatch(orderActions.setCustomerName('')); // Reset the customer name
        let skipFindInMenu = false;
        let orderItemsToUse: EntityMenuItem[] = orderItems || [];

        // Use brand menu cart:
        // 1. if both AI and HITL are using brand menu
        // 2. if pos cart is not available when AI uses brand menu but HITL uses PRP menu
        if (aiBrandMenuMode && hitlBrandMenuMode) {
          orderItemsToUse = convertBMOrderItemToEntityItem(
            brandMenuOrderItems || [],
            '',
            brandMenuConstraints
          );
        } else if (aiBrandMenuMode && !hitlBrandMenuMode) {
          // Skip find the hypothesis order item in the menu when AI and HITL are using different menu
          skipFindInMenu = true;
          if (orderItems && orderItems.length === 0) {
            // Use brand menu cart
            orderItemsToUse = convertBMOrderItemToEntityItem(
              brandMenuOrderItems || [],
              '',
              brandMenuConstraints
            );
          }
        }

        //Add loyalties added by AI in HITL cart
        loyalties.forEach((loyalty: ICartLoyalty) => {
          dispatch(
            addLoyaltiesCode({
              code: loyalty.code,
              addedBy: CartItemAdditionTypes.AI,
            })
          );
        });

        // Add customer info from AI hypothesis
        if (customer_info?.name) {
          dispatch(orderActions.setCustomerName(customer_info.name));
        }
        // Add items ordered by AI in HITL cart
        orderItemsToUse.forEach((orderItem) => {
          const { quantity } = orderItem;
          const transformedQuantity = Math.floor(quantity); //Added for temporary purpose only as quantity will never be in decimals

          importItemPromises.push(
            dispatch(
              addHypothesisItemToCart({
                orderItem: {
                  ...orderItem,
                  quantity: Math.min(
                    transformedQuantity,
                    MAX_CART_ITEM_QUANTITY
                  ),
                },
                skipFindInMenu,
              })
            ) as unknown as Promise<unknown>
          );
        });

        // Surface menu-module errors the AI reports for this hypothesis. The AI
        // only sends `menu_module_error` when the order failed to translate, so
        // an absent error clears any prior highlight (the AI may have corrected
        // the order). Errors are matched to the rebuilt cart by node name.
        // Gated by menu_module_error_ui (kill switch for the error surfacing).
        if (menuModuleErrorUiEnabled) {
          dispatch(
            orderActions.setMenuModuleCartErrors(
              menuModuleError ? normalizeMenuModuleError(menuModuleError) : []
            )
          );
        }
      });

      // Re-baseline to the rebuilt cart only after our own import thunks settle,
      // then reopen the edit-guard. A delete the agent makes after this point is
      // measured against the full imported cart, so a later marked re-import
      // cannot resurrect it (T2).
      if (importingRef.current) {
        Promise.all(importItemPromises).finally(() => {
          captureBaseline();
          importingRef.current = false;
        });
      }
    } else {
      // Outside auto mode the agent may edit the cart directly, so a later
      // identical frame must be re-applied rather than skipped.
      lastAppliedHypothesesRef.current = '';
    }
  }, [
    hypothesisFrame,
    isFinalOrderSubmitted,
    shouldAddHypothesisItems,
    isAIAutoMode,
    sbSessionId,
    hitlBrandMenuMode,
    menuModuleErrorUiEnabled,
  ]);
};

export default useHypothesisFrames;
